import { ArrowUpRight } from "lucide-react";

import { ImageZoom } from "@/components/ui/shadcn-io/image-zoom";
import { cn } from "@/lib/utils";
import type {
  ResolvedLineupCard,
  ResolvedVenueCard,
} from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";

const STATUS_CHIP: Record<ResolvedLineupCard["status"], string> = {
  linked:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  created: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",
  unmatched:
    "bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/30",
};

const STATUS_LABEL: Record<ResolvedLineupCard["status"], string> = {
  linked: "Linked",
  created: "Created",
  unmatched: "Unmatched",
};

function StatusChip({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * One side of an extracted → resolved comparison: a small uppercase column
 * label above its stacked values. Used by both VenueCard and LineupCard so the
 * two columns line up.
 */
function ComparisonColumn({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

export function VenueCard({
  card,
  openHref,
}: {
  card: ResolvedVenueCard;
  openHref?: string;
}) {
  const { resolved } = card;

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
          Venue
        </h3>
        <div className="flex items-center gap-2">
          {resolved.confidence !== undefined && (
            <StatusChip className="bg-muted text-muted-foreground">
              {Math.round(resolved.confidence * 100)}%
            </StatusChip>
          )}
          <StatusChip className={STATUS_CHIP.linked}>
            {card.statusLabel}
          </StatusChip>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ComparisonColumn label="Extracted">
          <span className="text-sm font-medium text-foreground">
            {card.extractedName ?? "—"}
          </span>
          <span className="text-xs text-muted-foreground">
            {card.extractedAddress ?? "—"}
          </span>
        </ComparisonColumn>

        <ComparisonColumn label="Resolved">
          {resolved.thumbnailUrl && (
            <ImageZoom className="w-16 shrink-0">
              <img
                src={resolved.thumbnailUrl}
                alt={resolved.name ?? "Venue"}
                className="h-16 w-16 rounded-md border object-cover"
              />
            </ImageZoom>
          )}
          <span className="text-sm font-medium text-foreground">
            {resolved.name ?? "—"}
          </span>
          {resolved.address && (
            <span className="text-xs text-muted-foreground">
              {resolved.address}
            </span>
          )}
          {resolved.city && (
            <span className="text-xs text-muted-foreground">
              {resolved.city}
            </span>
          )}
          {resolved.cid && (
            <span className="font-mono text-xs text-muted-foreground">
              CID {resolved.cid}
            </span>
          )}
          {openHref && resolved.id && (
            <a
              href={openHref}
              className="mt-1 inline-flex items-center gap-1 self-start text-xs font-medium text-blue-500 hover:underline"
            >
              Open in Mood
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          )}
        </ComparisonColumn>
      </div>
    </div>
  );
}

export function LineupCard({ cards }: { cards: ResolvedLineupCard[] }) {
  if (cards.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Lineup
      </h3>
      <ul className="flex flex-col gap-2">
        {cards.map((card) => (
          <li
            key={`${card.extractedName}-${card.status}`}
            className="grid grid-cols-2 gap-3 border-t pt-2 first:border-t-0 first:pt-0"
          >
            <ComparisonColumn label="Extracted">
              <span className="truncate text-sm text-foreground">
                {card.extractedName ?? "—"}
              </span>
            </ComparisonColumn>

            <ComparisonColumn label="Resolved">
              <div className="flex items-center gap-2">
                {card.resolved.thumbnailUrl ? (
                  <ImageZoom className="shrink-0">
                    <img
                      src={card.resolved.thumbnailUrl}
                      alt={card.resolved.name ?? card.extractedName ?? "Artist"}
                      className="h-10 w-10 rounded-md border object-cover"
                    />
                  </ImageZoom>
                ) : (
                  <span className="h-10 w-10 shrink-0 rounded-md border bg-muted" />
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-foreground">
                      {card.resolved.name ?? card.extractedName ?? "—"}
                    </span>
                    <StatusChip className={STATUS_CHIP[card.status]}>
                      {STATUS_LABEL[card.status]}
                    </StatusChip>
                  </div>
                  {card.resolved.subline && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {card.resolved.subline}
                    </span>
                  )}
                  {card.status === "unmatched" && card.reason && (
                    <span className="block truncate text-xs text-amber-700 dark:text-amber-300">
                      {card.reason}
                    </span>
                  )}
                </div>
                {card.resolved.id && (
                  <a
                    href={`/artists/edit/${card.resolved.id}`}
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-500 hover:underline"
                  >
                    Open
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </ComparisonColumn>
          </li>
        ))}
      </ul>
    </div>
  );
}
